import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon } from 'lucide-react';
import { DataTable, Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { mockPRs, mockApprovals, mockSuppliers, mockPOs } from '../data/mockData';
const mockAnnualPlanApprovals = [{
  id: 'plan-2025-26',
  fiscalYear: 'FY 2025/26',
  status: 'Submitted',
  totalBudget: 625000,
  totalItems: 12,
  submittedBy: 'Admin User',
  submittedDate: '2025-03-20',
  department: 'Procurement'
}, {
  id: 'plan-2024-25-v2',
  fiscalYear: 'FY 2024/25 (Revised)',
  status: 'Submitted',
  totalBudget: 710000,
  totalItems: 14,
  submittedBy: 'John Doe',
  submittedDate: '2024-11-05',
  department: 'Procurement'
}];
interface GenericApprovalListProps {
  type: 'supplier' | 'pr' | 'eoi' | 'rfp' | 'rfq' | 'noa' | 'po' | 'requisition' | 'annual-plan';
}
export function GenericApprovalList({
  type
}: GenericApprovalListProps) {
  const navigate = useNavigate();
  let title = '';
  let description = '';
  let data: any[] = [];
  let columns: Column<any>[] = [];
  let basePath = '';
  switch (type) {
    case 'supplier':
      title = 'Supplier Approval';
      description = 'Review and approve new supplier registrations.';
      data = mockSuppliers.filter(s => s.status === 'Pending');
      basePath = '/supplier-approval';
      columns = [{
        key: 'code',
        label: 'Supplier Code'
      }, {
        key: 'name',
        label: 'Supplier Name',
        render: s => <span className="font-medium text-gray-900" data-id="element-1025">{s.name}</span>
      }, {
        key: 'category',
        label: 'Category'
      }, {
        key: 'countryOfOrigin',
        label: 'Country'
      }, {
        key: 'status',
        label: 'Status',
        render: s => <StatusBadge status={s.status} data-id="element-1026" />
      }];
      break;
    case 'pr':
      title = 'Purchase Requisition Approval';
      description = 'Review and approve purchase requisitions.';
      data = mockPRs.filter(pr => pr.status === 'Submitted');
      basePath = '/pr-approval';
      columns = [{
        key: 'prNo',
        label: 'PR No'
      }, {
        key: 'departmentCode',
        label: 'Department'
      }, {
        key: 'procurementMethod',
        label: 'Method'
      }, {
        key: 'total',
        label: 'Total Amount',
        render: pr => `$${pr.total.toLocaleString()}`
      }, {
        key: 'status',
        label: 'Status',
        render: pr => <StatusBadge status={pr.status} data-id="element-1027" />
      }];
      break;
    case 'po':
      title = 'Purchase Order Approval';
      description = 'Review and approve purchase orders.';
      data = mockPOs.filter(po => po.status === 'Pending' || po.status === 'Draft');
      basePath = '/po-approval';
      columns = [{
        key: 'poNo',
        label: 'PO No'
      }, {
        key: 'vendor',
        label: 'Vendor'
      }, {
        key: 'totalAmount',
        label: 'Total Amount',
        render: po => `$${po.totalAmount.toLocaleString()}`
      }, {
        key: 'status',
        label: 'Status',
        render: po => <StatusBadge status={po.status} data-id="element-1028" />
      }];
      break;
    case 'annual-plan':
      title = 'Annual Plan Approval';
      description = 'Review and approve submitted annual procurement plans.';
      data = mockAnnualPlanApprovals;
      basePath = '/annual-plan-approval';
      columns = [{
        key: 'fiscalYear',
        label: 'Fiscal Year',
        render: item => <span className="font-medium text-indigo-600" data-id="element-1029">
              {item.fiscalYear}
            </span>
      }, {
        key: 'totalBudget',
        label: 'Total Amount',
        render: item => `$${item.totalBudget.toLocaleString()}`
      }, {
        key: 'totalItems',
        label: 'Items'
      }, {
        key: 'submittedBy',
        label: 'Submitted By'
      }, {
        key: 'submittedDate',
        label: 'Submitted Date'
      }, {
        key: 'status',
        label: 'Status',
        render: item => <StatusBadge status={item.status} data-id="element-1030" />
      }];
      break;
    default:
      // Handle EOI, RFP, RFQ, NOA
      title = `${type.toUpperCase()} Approval`;
      description = `Review and approve ${type.toUpperCase()} documents.`;
      data = mockApprovals.filter(a => a.refNo.toLowerCase().includes(type));
      basePath = `/${type}-approval`;
      columns = [{
        key: 'refNo',
        label: 'Reference No'
      }, {
        key: 'title',
        label: 'Title',
        render: a => <span className="font-medium text-gray-900" data-id="element-1031">{a.title}</span>
      }, {
        key: 'method',
        label: 'Procurement Method'
      }, {
        key: 'submittedBy',
        label: 'Submitted By'
      }, {
        key: 'status',
        label: 'Status',
        render: a => <StatusBadge status={a.status} data-id="element-1032" />
      }];
      break;
  }
  return <div className="space-y-6 h-full flex flex-col" data-id="element-1033">
      <div className="flex items-center justify-between" data-id="element-1034">
        <div data-id="element-1035">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1036">{title}</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1037">{description}</p>
        </div>
      </div>

      <div className="flex-1" data-id="element-1038">
        <DataTable columns={columns} data={data} keyExtractor={item => item.id} searchPlaceholder={`Search ${title}...`} onRowClick={item => navigate(`${basePath}/${item.id}`)} actions={item => <div className="flex space-x-3 justify-end" data-id="element-1040">
              <button onClick={() => navigate(`${basePath}/${item.id}`)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50" data-id="element-1041">
                <EyeIcon className="h-3.5 w-3.5 mr-1" data-id="element-1042" /> Review
              </button>
            </div>} data-id="element-1039" />
      </div>
    </div>;
}